import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, ChatCircle, PaperPlaneRight, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const PostCard = ({ post, onLike }) => {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [likes, setLikes] = useState(post.likes_count);
  const [liked, setLiked] = useState((post.likes || []).includes(user?.user_id));

  const loadComments = async () => {
    const r = await api.get(`/posts/${post.id}/comments`);
    setComments(r.data);
  };

  const toggleComments = async () => {
    setShowComments((s) => !s);
    if (!showComments) await loadComments();
  };

  const like = async () => {
    const r = await api.post(`/posts/${post.id}/like`);
    setLiked(r.data.liked);
    setLikes(r.data.likes_count);
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const r = await api.post(`/posts/${post.id}/comments`, { content: text });
    setComments((c) => [...c, r.data]);
    setText("");
  };

  return (
    <article className="brut-card" data-testid={`post-${post.id}`}>
      <div className="p-5 flex items-center gap-3 border-b border-line">
        <Link to={`/app/profile/${post.author?.user_id}`}>
          <img src={post.author?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${post.author?.user_id}`} className="w-10 h-10 border border-line object-cover" alt="" />
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/app/profile/${post.author?.user_id}`} className="font-heading font-bold text-base hover:underline">{post.author?.name}</Link>
          <div className="text-xs text-ink-3 truncate">{post.author?.headline || "Member"}</div>
        </div>
        <div className="label-tag text-ink-3">{new Date(post.created_at).toLocaleDateString()}</div>
      </div>
      <div className="p-5">
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>
      </div>
      {post.image_url ? <img src={post.image_url} alt="" className="w-full max-h-[480px] object-cover border-t border-line" /> : null}
      <div className="px-5 py-3 border-t border-line flex items-center gap-6 text-sm">
        <button onClick={like} className="flex items-center gap-2 hover:text-signal-red transition-colors" data-testid={`like-${post.id}`}>
          <Heart size={18} weight={liked ? "fill" : "regular"} color={liked ? "#FF3B30" : undefined} />
          <span>{likes}</span>
        </button>
        <button onClick={toggleComments} className="flex items-center gap-2 hover:text-ink transition-colors" data-testid={`comment-${post.id}`}>
          <ChatCircle size={18} />
          <span>{post.comments_count}</span>
        </button>
      </div>
      {showComments && (
        <div className="border-t border-line bg-paper-tint">
          <div className="max-h-64 overflow-y-auto">
            {comments.map((c) => (
              <div key={c.id} className="px-5 py-3 border-b border-line flex gap-3">
                <img src={c.author?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${c.author?.user_id}`} className="w-7 h-7 border border-line" alt="" />
                <div className="flex-1">
                  <div className="text-xs font-semibold">{c.author?.name}</div>
                  <div className="text-sm">{c.content}</div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={submitComment} className="flex border-t border-line">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write a comment…"
              data-testid={`comment-input-${post.id}`}
              className="flex-1 px-4 py-3 bg-white text-sm border-r border-line focus:bg-white"
            />
            <button type="submit" className="px-5 bg-ink text-white" data-testid={`comment-submit-${post.id}`}>
              <PaperPlaneRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      )}
    </article>
  );
};

const Feed = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [showImage, setShowImage] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const r = await api.get("/posts");
    setPosts(r.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    try {
      const r = await api.post("/posts", { content, image_url: imageUrl });
      setPosts((p) => [r.data, ...p]);
      setContent(""); setImageUrl(""); setShowImage(false);
      toast.success("POSTED");
    } catch {
      toast.error("Failed to post");
    }
  };

  return (
    <div className="grid grid-cols-12 min-h-screen">
      <section className="col-span-12 lg:col-span-8 border-r border-line">
        <header className="border-b border-line p-6 flex items-end justify-between">
          <div>
            <div className="label-tag text-ink-3">// 01 — FEED</div>
            <h1 className="font-heading font-black text-4xl mt-1">THE WIRE</h1>
          </div>
          <div className="text-xs text-ink-3">{posts.length} POSTS</div>
        </header>

        <form onSubmit={submit} className="border-b border-line p-6 bg-white" data-testid="post-form">
          <div className="flex gap-3">
            <img src={user?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${user?.user_id}`} className="w-10 h-10 border border-line" alt="" />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Broadcast a thought…"
              rows={3}
              data-testid="post-input"
              className="flex-1 border border-line p-3 text-sm resize-none focus:border-ink"
            />
          </div>
          {showImage && (
            <input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Image URL"
              data-testid="post-image-input"
              className="mt-3 w-full border border-line p-3 text-sm focus:border-ink"
            />
          )}
          <div className="mt-3 flex items-center justify-between">
            <button type="button" onClick={() => setShowImage(s => !s)} className="text-xs flex items-center gap-2 text-ink-2 hover:text-ink">
              <ImageIcon size={14} /> {showImage ? "REMOVE IMAGE" : "ADD IMAGE"}
            </button>
            <button type="submit" className="btn-primary" data-testid="post-submit">PUBLISH</button>
          </div>
        </form>

        <div className="divide-y divide-line">
          {loading ? <div className="p-8 text-sm text-ink-3">LOADING…</div> :
            posts.length === 0 ? <div className="p-12 text-center text-ink-3 text-sm">— No posts yet. Be first to broadcast. —</div> :
            posts.map((p) => <PostCard key={p.id} post={p} />)}
        </div>
      </section>

      <aside className="hidden lg:block col-span-4 p-6 bg-paper">
        <div className="brut-card p-6">
          <div className="label-tag text-ink-3">// 02 — STATUS</div>
          <h3 className="font-heading font-black text-2xl mt-1">YOUR FEED</h3>
          <div className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between border-b border-line pb-2"><span>Profile</span><span className="text-ink-3">{user?.headline ? "OK" : "INCOMPLETE"}</span></div>
            <div className="flex justify-between border-b border-line pb-2"><span>Posts</span><span className="text-ink-3">{posts.length}</span></div>
          </div>
        </div>
        <div className="brut-card p-6 mt-4">
          <div className="label-tag text-ink-3">// TIP</div>
          <p className="text-sm mt-2 leading-relaxed">Press <kbd className="border border-line px-1 text-xs">⌘</kbd> + click on a profile to connect instantly.</p>
        </div>
      </aside>
    </div>
  );
};

export default Feed;
